import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { del, get, set } from 'idb-keyval';
import { ApiError } from '~/lib/api';
import { registerMutationDefaults } from './mutations';

const WEEK = 7 * 24 * 3600_000;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: WEEK,
      // serve cached data instantly and work from cache when offline
      networkMode: 'offlineFirst',
      retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
    },
    mutations: { networkMode: 'online' },
  },
});

registerMutationDefaults(queryClient);

export const persister = createAsyncStoragePersister({
  key: 'hudhuria-cache',
  throttleTime: 800,
  storage: { getItem: (k) => get(k), setItem: (k, v) => set(k, v), removeItem: (k) => del(k) },
});

/** Persist reference data + every write still waiting to sync. Exports and team lists are never cached. */
export const persistOptions = {
  persister,
  maxAge: WEEK,
  buster: 'hud-1',
  dehydrateOptions: {
    shouldDehydrateQuery: (q: { state: { status: string }; queryKey: readonly unknown[] }) =>
      q.state.status === 'success' && !['export', 'team'].includes(String(q.queryKey[0])),
    shouldDehydrateMutation: (m: { state: { status: string } }) => m.state.status === 'pending',
  },
};

/** After the cache is restored: replay queued writes (paused ones and ones that were mid-retry). */
export async function resumeQueuedWrites() {
  await queryClient.resumePausedMutations();
  for (const m of queryClient.getMutationCache().getAll()) {
    if (m.state.status === 'pending' && !m.state.isPaused) void m.continue();
  }
}

/** Wipe local data (sign-out on a shared device). */
export async function clearLocalData() {
  queryClient.getMutationCache().clear();
  queryClient.clear();
  await persister.removeClient();
}
